import { useState, useEffect } from 'react';
import { usePlatform, ScreenOrientation } from '~/services/platform';

interface UseOrientationResult {
  /** Current orientation (auto-detected or override) */
  orientation: ScreenOrientation;
  /** Auto-detected orientation */
  detectedOrientation: ScreenOrientation;
  /** Whether there's a manual override active */
  hasOverride: boolean;
  /** Set manual override (null to use auto-detect) */
  setOverride: (orientation: ScreenOrientation | null) => void;
  /** Whether the platform supports orientation changes */
  supportsChange: boolean;
}

/**
 * Hook for managing screen orientation.
 * Provides auto-detection and manual override capabilities.
 *
 * @example
 * ```tsx
 * const { orientation, detectedOrientation, setOverride } = useOrientation();
 *
 * // Auto-detected orientation
 * console.log(detectedOrientation); // 'landscape' or 'portrait'
 *
 * // Override with manual selection
 * setOverride('portrait');
 *
 * // Final orientation (override ?? detected)
 * console.log(orientation); // 'portrait'
 *
 * // Clear override (back to auto-detect)
 * setOverride(null);
 * ```
 */
export const useOrientation = (): UseOrientationResult => {
  const platform = usePlatform();
  const [detectedOrientation, setDetectedOrientation] =
    useState<ScreenOrientation>(platform.getOrientation());
  const [override, setOverride] = useState<ScreenOrientation | null>(null);

  useEffect(() => {
    // Subscribe to orientation changes
    const unsubscribe = platform.onOrientationChange((newOrientation) => {
      setDetectedOrientation(newOrientation);
    });

    setDetectedOrientation(platform.getOrientation());

    return unsubscribe;
  }, [platform]);

  return {
    orientation: override ?? detectedOrientation,
    detectedOrientation,
    hasOverride: override !== null,
    setOverride,
    supportsChange: platform.supportsOrientationChange(),
  };
};

export default useOrientation;
